import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Heart, Users, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { clubs } from '../mockData';

const Clubs = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Clubs & <span className="text-[#E6B933]">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Extracurricular programs, community service opportunities, and specialized development for student-athletes.
            </p>
          </div>
        </div>
      </section>

      {/* Clubs Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Programs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Structured activities that build skills, create opportunities, and develop leaders
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {clubs.map((club, index) => {
              const CardContent = (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={club.image} 
                      alt={club.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{club.name}</h3>
                    <p className="text-gray-600 leading-relaxed">{club.description}</p>
                    {club.link && (
                      <span className="inline-block mt-4 text-[#E6B933] font-semibold">Learn More →</span>
                    )}
                  </div>
                </Card>
              );
              
              return club.link ? (
                <Link key={index} to={club.link}>{CardContent}</Link>
              ) : (
                CardContent
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Clubs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Clubs</h2>
            <p className="text-xl text-gray-600">Specialized programs for focused development</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                <Trophy className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Be Immortal</h3>
              <p className="text-sm text-[#E6B933] font-semibold mb-4">Life Skills & Leadership</p>
              <p className="text-gray-600 mb-6">Build the mindset, discipline, and character to thrive in school, sports, and life.</p>
              <Link to="/clubs/be-immortal">
                <button className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-all duration-300">
                  Learn More
                </button>
              </Link>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                <TrendingUp className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">The Mogul Manifest</h3>
              <p className="text-sm text-[#E6B933] font-semibold mb-4">Entrepreneurship & Innovation</p>
              <p className="text-gray-600 mb-6">Turn ideas into action and build opportunities for yourself and your community.</p>
              <Link to="/clubs/mogul-manifest">
                <button className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-all duration-300">
                  Learn More
                </button>
              </Link>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                <Users className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">The Prosperity Project</h3>
              <p className="text-sm text-[#E6B933] font-semibold mb-4">Financial Literacy & Wealth Building</p>
              <p className="text-gray-600 mb-6">Learn financial skills schools don&apos;t teach and build wealth early.</p>
              <Link to="/clubs/prosperity-project">
                <button className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 rounded-lg transition-all duration-300">
                  Learn More
                </button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Additional Services</h2>
            <p className="text-xl text-gray-600">Comprehensive services for holistic youth development</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <a href="https://missingelementsports.com" target="_blank" rel="noopener noreferrer">
              <Card className="p-8 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                  <Trophy className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Athletic Development</h3>
                <p className="text-gray-600 mb-4">Basketball, track, football, and multi-sport training programs</p>
                <span className="text-[#E6B933] font-semibold">Visit Site →</span>
              </Card>
            </a>

            <Link to="/community-service">
              <Card className="p-8 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                  <Heart className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Community Service</h3>
                <p className="text-gray-600 mb-4">Feed The Youth, neighborhood cleanups, and civic projects</p>
                <span className="text-[#E6B933] font-semibold">View Events →</span>
              </Card>
            </Link>

            <Link to="/clubs/all">
              <Card className="p-8 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                  <Users className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Extracurricular Clubs</h3>
                <p className="text-gray-600 mb-4">Leadership, entrepreneurship, and financial literacy programs</p>
                <span className="text-[#E6B933] font-semibold">View All Clubs →</span>
              </Card>
            </Link>

            <Card className="p-8 text-center hover:shadow-lg transition-all duration-300 group">
              <div className="w-16 h-16 bg-[#E6B933]/10 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                <TrendingUp className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">NIL Education</h3>
              <p className="text-gray-600">Personal branding, marketing, and value-building for athletes</p>
            </Card>
          </div>
        </div>
      </section>

      {/* NIL Program Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                NIL Development for <span className="text-[#E6B933]">Student-Athletes</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Name, Image, and Likeness (NIL) rights have transformed college athletics. Our specialized program ensures student-athletes understand how to protect and maximize their brand.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#E6B933] mt-2"></div>
                  <p className="text-gray-600">Personal branding and identity development</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#E6B933] mt-2"></div>
                  <p className="text-gray-600">Social media strategy and content creation</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#E6B933] mt-2"></div>
                  <p className="text-gray-600">Understanding NIL contracts and agreements</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#E6B933] mt-2"></div>
                  <p className="text-gray-600">Connecting with local businesses and sponsors</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-[#E6B933] mt-2"></div>
                  <p className="text-gray-600">Financial literacy for NIL earnings</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/sysufm8j_ME%2012u.png" 
                alt="Youth sports team" 
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_why-me-org/artifacts/ru0xvpf8_ME%20TRACK%201.png" 
                alt="Track athletes" 
                className="rounded-lg shadow-lg w-full h-64 object-cover mt-8 hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Involved?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join our clubs and programs to develop skills, build relationships, and create opportunities.
          </p>
          <a href="/get-involved">
            <button className="bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105">
              Sign Up Today
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Clubs;
